import { hasOwn } from "../../commonFunctions/arrayFunc";
import { SCREEN_WIDTHS } from "../../constant/constant";
import { generateObjectId } from "../../utils/objectIdUtils";

export const removeItemById = (layout, ids) => {
  const idSet = new Set(Array.isArray(ids) ? ids : [ids]);

  return layout.reduce((acc, item) => {
    if (idSet.has(item.id)) {
      return acc; // Skip matched ID(s)
    }

    let updatedItem = { ...item };

    // Recursively update tab children
    if (item.type === "tab" && Array.isArray(item.tabs)) {
      updatedItem.tabs = item.tabs.map((tab) => ({
        ...tab,
        children: removeItemById(tab.children || [], ids)
      }));
    }

    // Recursively update children
    if (Array.isArray(item.children)) {
      updatedItem.children = removeItemById(item.children, ids);
    }

    return [...acc, updatedItem];
  }, []);
};

// Add item to parent by ID
export const addItemToParent = (layout, itemToAdd, parentId) => {
  return layout.map((item) => {
    if (item.id === parentId) {
      return {
        ...item,
        children: [...item.children, ...(Array.isArray(itemToAdd) ? itemToAdd : [itemToAdd])]
      };
    }

    // If the item is of type "tab" and has tabs, look through each tab's children for the parentId
    if (item.type === "tab" && item.tabs && item.tabs.length > 0) {
      const updatedTabs = item.tabs.map((tab) => ({
        ...tab,
        children: addItemToParent(tab.children || [], itemToAdd, parentId)
      }));
      return { ...item, tabs: updatedTabs };
    }

    // Recursive call if the item has regular children
    if (item.children && item.children.length > 0) {
      return {
        ...item,
        children: addItemToParent(item.children, itemToAdd, parentId)
      };
    }

    return item; // Return the item unmodified if no match is found
  });
};

export function reflowVerticalElements(elements) {
  let currentTop = 0;

  return elements.map((el) => {
    const height = parseInt(el.style.height || "0", 10);

    const updated = {
      ...el,
      style: {
        ...el.style,
        left: "0px",
        top: `${currentTop}px`
      }
    };

    currentTop += height;
    return updated;
  });
}

export const duplicateItemAsSibling = (layout, selectedItems, gridSize) => {
  if (!Array.isArray(selectedItems)) {
    selectedItems = [selectedItems];
  }
  let newItems = []; // Variable to hold the duplicated items

  const updatedLayout = layout.map((item) => {
    if (item.children && item.children.length > 0) {
      // Check how many selected items are present in the children
      const selectedChildren = item.children.filter((child) =>
        selectedItems.some((selected) => selected.id === child.id)
      );

      if (selectedChildren.length > 0) {
        let updatedChildren = [...item.children];

        selectedChildren.forEach((selectedChild) => {
          const deepCopy = getDeepCopy(selectedChild);
          const updatedItem = replaceIdsInPositionedLayout(deepCopy);

          // Adjust the position of the duplicated item
          updatedItem.style = {
            ...updatedItem.style,
            left: `${parseInt(updatedItem.style.left, 10) + gridSize}px`,
            ...(updatedItem.type !== "column" && {
              top: `${parseInt(updatedItem.style.top, 10) + gridSize}px`
            })
          };

          // Add the updated item at the end of the children array
          updatedChildren = [
            ...updatedChildren,
            { ...updatedItem } // Add the new item at the end
          ];

          // Add the new item to the newItems array
          newItems.push(updatedItem);
        });

        // Return the updated item with new children
        return {
          ...item,
          children: updatedChildren
        };
      }

      // // Recurse into children if no match found here
      // return {
      //   ...item,
      //   children: duplicateItemAsSibling(item.children, selectedItems).layout
      // };
      const { layout: updatedChildrenLayout, items: updatedChildrenItems } = duplicateItemAsSibling(
        item.children,
        selectedItems,
        gridSize
      );

      // Accumulate the new items from the recursive call
      newItems = [...newItems, ...updatedChildrenItems];

      return {
        ...item,
        children: updatedChildrenLayout // Only return the layout part for children
      };
    }

    if (item.type === "tab" && item.tabs && item.tabs.length > 0) {
      const updatedTabs = item.tabs.map((tab) => {
        const { layout: updatedTabChildrenLayout, items: updatedTabChildrenItems } = duplicateItemAsSibling(
          tab.children || [],
          selectedItems,
          gridSize
        );

        // Accumulate new items from the recursive call
        newItems = [...newItems, ...updatedTabChildrenItems];

        return {
          ...tab,
          children: updatedTabChildrenLayout // Only return the layout part for tabs' children
        };
      });
      return { ...item, tabs: updatedTabs };
    }

    return item; // Return the item unmodified if no match is found
  });
  // Return both the updated layout and the newly created items
  return { layout: updatedLayout, items: newItems };
};

export const multiDuplicateElements = (layout, selected, gridSize) => {
  let newLayoutConfig = { ...layout };
  let duplicateItems = {};
  const grouped = groupItemsByParentId(selected);

  Object.entries(grouped).forEach(([id, { path, items }]) => {
    if (path) {
      const mainElement = findItemById(layout || [], id);
      const data = getValueByPath(mainElement, path[1]);
      const { layout: newLayout, items: newItems } = duplicateItemAsSibling(data, items, gridSize);
      const config = setValueByPath(mainElement, path[1], newLayout);
      newLayoutConfig = updateBlockInLayout(layout, config);
      // duplicateItems = [...duplicateItems, ...newItems];
      duplicateItems = {
        ...duplicateItems,
        ...newItems.reduce((pre, curr) => {
          return {
            ...pre,
            [curr.id]: { item: curr, otherInfo: { path } }
          };
        }, {})
      };
    } else {
      const { layout: newLayout, items: newItems } = duplicateItemAsSibling(layout, items, gridSize);
      newLayoutConfig = newLayout;
      duplicateItems = {
        ...duplicateItems,
        ...newItems.reduce((pre, curr) => {
          return {
            ...pre,
            [curr.id]: { item: curr }
          };
        }, {})
      };
    }
  });
  return { layout: newLayoutConfig, items: duplicateItems };
};

export const findItemById = (layout, id) => {
  for (const item of layout) {
    if (item.id === id) {
      return item; // Return the item if the id matches
    }

    // If the item has tabs, search within each tab's children
    if (item.type === "tab" && item.tabs && item.tabs.length > 0) {
      for (const tab of item.tabs) {
        const foundItem = findItemById(tab.children || [], id); // Recursive call on tab's children
        if (foundItem) {
          return foundItem;
        }
      }
    }

    // If the item has children, search within them
    if (item.children && item.children.length > 0) {
      const foundItem = findItemById(item.children, id);
      if (foundItem) {
        return foundItem;
      }
    }
  }

  return null; // Return null if no matching item is found
};

export const getItemsWithParents = (layout, ids) => {
  const result = {};

  const dfs = (items, parentId = null) => {
    for (const item of items) {
      if (ids.includes(item.id)) {
        result[item.id] = {
          item,
          parentId
        };
      }

      // Search in tabbed children
      if (item.type === "tab" && Array.isArray(item.tabs)) {
        for (const tab of item.tabs) {
          if (Array.isArray(tab.children)) {
            dfs(tab.children, item.id);
          }
        }
      }

      // Search in regular children
      if (Array.isArray(item.children)) {
        dfs(item.children, item.id);
      }
    }
  };

  dfs(layout);

  return result;
};

const handleTableConfig = (config, result) => {
  if (config && config.config && config.config.rows) {
    const { rows } = config.config;
    rows.forEach((row) => {
      row.forEach((cell) => {
        if (cell.custom_text === "") {
          const fieldId = cell.id;
          addToResult(result, "field", fieldId);
        }
      });
    });
  }
};

const handleFieldConfig = (config, result, key = "field") => {
  if (config && config.id) {
    const { id } = config;
    addToResult(result, key, id);
    const selectedContainerTypes = ["recordset-list", "grid", "recordset"];
    if (selectedContainerTypes.includes(config.field_type) && config.catalog && Array.isArray(config.catalog.layout)) {
      const richColumnIds = getElementIds(config.catalog.layout);
      mergeIdsData(richColumnIds, result);
    }
    if (
      selectedContainerTypes.includes(config.field_type) &&
      config.table_fields &&
      Array.isArray(config.table_fields)
    ) {
      config.table_fields.forEach((table_field) => {
        handleFieldConfig(table_field, result);
      });
    }
  }
};

const handleFieldConfigWithObject = (config, id, result, key = "field") => {
  if (config && id) {
    if (!result[key]) {
      result[key] = {};
    }
    if (!result[key][id]) {
      result[key][id] = config;
    }
  }
};

const handleFieldMasterDataIds = (config, result) => {
  if (config && config.color_config_type && config.color_config_type === "masterdata" && config.color_master_data) {
    const { color_master_data } = config;
    addToResult(result, "masterdata", color_master_data);
  }
};

const handleRvTableConfig = (config, result) => {
  if (config && Array.isArray(config.columns)) {
    config.columns.forEach((column) => {
      if (column.type === "field") {
        handleFieldConfig(column, result);
        handleFieldMasterDataIds(column.config || {}, result);
      }
      if (column.type === "custom" && column.custom_type === "rich_column") {
        if (column.layout_config && Array.isArray(column.layout_config.layouts)) {
          const richColumnIds = getElementIds(column.layout_config.layouts);
          mergeIdsData(richColumnIds, result);
        }
      }
    });
  }
  if (config && config.catalog && Array.isArray(config.catalog.layout)) {
    const catalogIds = getElementIds(config.catalog.layout);
    mergeIdsData(catalogIds, result);
  }
};

const handleRvFiltersConfig = (config, result) => {
  if (config && Array.isArray(config.filters)) {
    config.filters.forEach((filter) => {
      handleFieldConfig(filter, result, "rv_filters");
    });
  }
};

const handleRvTabFiltersConfig = (config, result) => {
  if (config.filter_field) {
    handleFieldConfig(config.filter_field, result, "rv_tab_filters");
  }
};

const handleChartConfig = (config, result) => {
  if (!config) return;
  addToResult(result, "chart_template_ids", config.chart_template_id || "");
};

const mergeIdsData = (source, result) => {
  for (let key in source) {
    if (hasOwn(source, key)) {
      if (result[key]) {
        result[key] = Array.from(new Set([...result[key], ...source[key]]));
      } else {
        result[key] = [...source[key]];
      }
    }
  }
};

const handleProgressTrackerConfig = (config, result) => {
  if (config && config.completed_value && config.completed_value.value) {
    handleFieldConfig(config.completed_value.value, result);
  }
  if (config && config.total_value && config.total_value.value) {
    handleFieldConfig(config.total_value.value, result);
  }
};
const handleCustomComponentConfig = (config, result) => {
  if (config && typeof config === "object" && Array.isArray(config.props_mapping)) {
    config.props_mapping.forEach((mapping) => {
      if (mapping && typeof mapping === "object" && mapping.type === "mapped" && mapping.value) {
        addToResult(result, "field", mapping.value);
      }
    });
  }
};

const handleMapConfig = (config, result) => {
  if (config) {
    if (config?.location_point) {
      addToResult(result, "map", config?.location_point);
    }
    if (config.card && config.card.layout && Array.isArray(config.card.layout)) {
      const richColumnIds = getElementIds(config.card.layout);
      mergeIdsData(richColumnIds, result);
    }
  }
};

const handleFormFieldConfig = (config, result) => {
  if (!config || !config.field || !config.field.id) {
    return;
  }
  addToResult(result, "form-field", config.field.id);
};
const handleElementConfig = (item, result) => {
  if (item.config) {
    const { id, type } = item.config;
    if (type === "table") {
      handleTableConfig(item.config, result);
    } else if (type === "field") {
      handleFieldConfig(item.config, result);
      handleFieldMasterDataIds(item.config.config || {}, result);
    } else if (type === "rv_table") {
      handleRvTableConfig(item.config, result);
    } else if (type === "rv_filters") {
      handleRvFiltersConfig(item.config, result); //
    } else if (type === "rv_tab_filters") {
      handleRvTabFiltersConfig(item.config, result); //
    } else if (type === "rv_tile" || type === "rv_chart") {
      handleFieldConfigWithObject(item.config, item.id, result, type);
      handleChartConfig(item.config, result);
    } else if (type === "chart") {
      handleChartConfig(item.config, result);
    } else if (type === "map") {
      handleMapConfig(item.config, result);
    } else if (type === "form-field") {
      handleFormFieldConfig(item.config, result);
    } else if (type === "file_viewer") {
      if (item.config.preview_field) {
        handleFieldConfig(item.config.preview_field, result);
      }
    } else if (type === "progress-tracker") {
      handleProgressTrackerConfig(item.config, result);
    } else if (type === "custom-component") {
      handleCustomComponentConfig(item.config, result);
    } else {
      addToResult(result, type, id);
    }
  }
};

const addToResult = (result, type, id) => {
  if (!id || !type) return;

  if (!result[type]) {
    result[type] = [];
  }

  if (!result[type].includes(id)) {
    result[type].push(id);
  }
};

const handleBlockColumnConfig = (item, result) => {
  if (!item || typeof item !== "object") return;
  if (!result) return;

  const bgImageField = item.config?.bg_image_field || null;
  if (item.config) {
    addToResult(result, "field", item.config.colorif_field || "");
    if (item.config.color_config_type && item.config.color_config_type === "masterdata") {
      addToResult(result, "masterdata", item.config.color_master_data || "");
    }
  }
  handleFieldConfig(bgImageField, result);
};

const handleVisibilityFieldsConfig = (item, result) => {
  if (!item || typeof item !== "object") return;
  if (!result) return;
  const visibilityConfig = item.config?.visibility || null;

  if (visibilityConfig) {
    if (visibilityConfig?.rules && Array.isArray(visibilityConfig.rules)) {
      visibilityConfig.rules.forEach((rule) => {
        if (rule.field_id) {
          addToResult(result, "field", rule.field_id);
        }
      });
    }
  }
};

export const getElementIds = (layout) => {
  const result = {};

  const traverse = (items) => {
    for (const item of items) {
      if (item.type === "element") {
        handleElementConfig(item, result);
      }
      if (item.type === "block" || item.type === "column") {
        handleBlockColumnConfig(item, result);
      }

      if (item.type === "block" || item.type === "column" || item.type === "section") {
        handleVisibilityFieldsConfig(item, result);
      }

      if (item.children && item.children.length > 0) {
        traverse(item.children);
      }

      if (item.type === "tab" && item.tabs && item.tabs.length > 0) {
        for (const tab of item.tabs) {
          traverse(tab.children || []);
        }
      }
    }
  };

  traverse(layout);
  return result;
};

export const updateBlockInLayout = (layout, updatedNodeOrNodes) => {
  if (!Array.isArray(updatedNodeOrNodes)) {
    updatedNodeOrNodes = [updatedNodeOrNodes];
  }

  const updatedNodeMap = new Map(updatedNodeOrNodes.map((node) => [node.id, node]));

  const updateNode = (node) => {
    if (updatedNodeMap.has(node.id)) {
      return updatedNodeMap.get(node.id);
    }

    if (node.type === "tab" && node.tabs) {
      return {
        ...node,
        tabs: node.tabs.map((tab) => ({
          ...tab,
          children: tab.children ? updateBlockInLayout(tab.children, updatedNodeOrNodes) : []
        }))
      };
    }

    if (node.children) {
      return {
        ...node,
        children: updateBlockInLayout(node.children, updatedNodeOrNodes)
      };
    }

    return node;
  };

  return layout.map(updateNode);
};

export const replaceIdsInPositionedLayout = (layout) => {
  if (Array.isArray(layout)) {
    return layout.map(replaceIdsInPositionedLayout);
  }

  if (typeof layout === "object" && layout !== null) {
    const newNode = { ...layout, id: generateObjectId() };

    if (layout.tabs && Array.isArray(layout.tabs)) {
      newNode.tabs = layout.tabs.map(replaceIdsInPositionedLayout);
    }

    if (layout.children) {
      newNode.children = replaceIdsInPositionedLayout(layout.children);
    }

    return newNode;
  }

  return layout;
};

export const updateOnlyElementConfigInLayout = (layout, id, config) => {
  return layout.map((node) => {
    if (node.id === id) {
      if (node.type === "tab") {
        return {
          ...node,
          tabs: config
        };
      } else {
        return { ...node, config };
      }
    }

    if (node.type === "tab" && node.tabs && node.tabs.length > 0) {
      const updatedTabs = node.tabs.map((tab) => ({
        ...tab,
        children: updateOnlyElementConfigInLayout(tab.children || [], id, config)
      }));
      return { ...node, tabs: updatedTabs };
    }

    if (node.children) {
      return {
        ...node,
        children: updateOnlyElementConfigInLayout(node.children, id, config)
      };
    }

    return node;
  });
};
export const updateClassesInLayout = (layout, idOrIds, classes) => {
  const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];

  return layout.map((node) => {
    // Match node by id
    if (ids.includes(node.id)) {
      return {
        ...node,
        classes: classes
      };
    }

    // Handle nested tabs
    if (node.type === "tab" && Array.isArray(node.tabs)) {
      const updatedTabs = node.tabs.map((tab) => ({
        ...tab,
        children: updateClassesInLayout(tab.children || [], ids, classes)
      }));
      return { ...node, tabs: updatedTabs };
    }

    // Handle nested children
    if (Array.isArray(node.children)) {
      return {
        ...node,
        children: updateClassesInLayout(node.children, ids, classes)
      };
    }

    return node;
  });
};

export const updateStyleInLayout = (layout, id, style) => {
  return layout.map((node) => {
    if (node.id === id) {
      return {
        ...node,
        style: {
          ...node.style,
          ...style
        }
      };
    }

    // If the node has nested tabs, recursively update the classes in each tab
    if (node.type === "tab" && node.tabs && node.tabs.length > 0) {
      const updatedTabs = node.tabs.map((tab) => ({
        ...tab,
        children: updateStyleInLayout(tab.children || [], id, style)
      }));
      return { ...node, tabs: updatedTabs };
    }

    if (node.children) {
      return {
        ...node,
        children: updateStyleInLayout(node.children, id, style)
      };
    }

    return node;
  });
};

export const resizeSelectedItem = (layoutConfig, selectedItem, direction, gridSize, otherInfo) => {
  if (!selectedItem) return layoutConfig;
  let selected = findItemById(layoutConfig, selectedItem.id);
  let pathArray;
  let mainElement;
  if (!selected && otherInfo && otherInfo.path) {
    mainElement = findItemById(layoutConfig || [], otherInfo.path[0]);
    pathArray = getValueByPath(mainElement, otherInfo.path[1]);
    selected = findItemById(pathArray || [], selectedItem.id);
  }
  const updatedItem = JSON.parse(JSON.stringify(selected));
  const currentWidth = parseInt(updatedItem.style.width || "0", 10);
  const currentHeight = parseInt(updatedItem.style.height || "0", 10);
  if (updatedItem) {
    if (direction === "right") {
      updatedItem.style.width = `${currentWidth + gridSize}px`;
    }
    if (direction === "down") {
      updatedItem.style.height = `${currentHeight + gridSize}px`;
    }
    if (direction === "up") {
      updatedItem.style.height = `${currentHeight - gridSize}px`;
    }
    if (direction === "left") {
      updatedItem.style.width = `${currentWidth - gridSize}px`;
    }
  }
  if (mainElement && pathArray && otherInfo && otherInfo.path) {
    const newLayout = updateBlockInLayout(pathArray, updatedItem);
    const config = setValueByPath(mainElement, otherInfo.path[1], newLayout);
    return updateBlockInLayout(layoutConfig, config);
  } else {
    return updateBlockInLayout(layoutConfig, updatedItem);
  }
};

export const moveSelectedItem = (layoutConfig, selected, direction, gridSize, otherInfo) => {
  if (!selected) return layoutConfig;
  if (selected.type === "section" && (direction === "up" || direction === "down")) {
    const arr = [...layoutConfig];
    const index = layoutConfig.findIndex((item) => item.id === selected.id);
    if (index !== -1) {
      const newIndex = direction === "up" ? index - 1 : index + 1;

      if (newIndex < 0 || newIndex >= arr.length) return arr;
      const [movedItem] = arr.splice(index, 1);
      arr.splice(newIndex, 0, movedItem);
    }
    return arr;
  }
  if (selected.type === "section") return layoutConfig;
  let pathArray;
  let mainElement;
  if (selected && otherInfo && otherInfo.path) {
    mainElement = findItemById(layoutConfig || [], otherInfo.path[0]);
    pathArray = getValueByPath(mainElement, otherInfo.path[1]);
    selected = findItemById(pathArray || [], selected.id);
  }
  const updatedItem = moveSelectedItemInLayout(selected, direction, gridSize);
  if (mainElement && pathArray && otherInfo && otherInfo.path) {
    const newLayout = updateBlockInLayout(pathArray, updatedItem);
    const config = setValueByPath(mainElement, otherInfo.path[1], newLayout);
    return updateBlockInLayout(layoutConfig, config);
  } else {
    return updateBlockInLayout(layoutConfig, updatedItem);
  }
};

function moveSelectedItemInLayout(selectedItem, direction, gridSize) {
  const updatedItem = { ...selectedItem, style: { ...selectedItem.style } };
  const type = updatedItem.type;
  if (type === "section") {
    return updatedItem;
  }
  const isColumn = type === "column";
  const currentTop = parseInt(updatedItem.style.top || 0, 10);
  const currentLeft = parseInt(updatedItem.style.left || 0, 10);
  switch (direction) {
    case "up":
      if (isColumn) return updatedItem;
      updatedItem.style.top = `${currentTop - gridSize}px`;
      break;
    case "down":
      if (isColumn) return updatedItem;
      updatedItem.style.top = `${currentTop + gridSize}px`;
      break;
    case "left":
      updatedItem.style.left = `${currentLeft - gridSize}px`;
      break;
    case "right":
      updatedItem.style.left = `${currentLeft + gridSize}px`;
      break;
    default:
      break;
  }
  return updatedItem;
}

export const moveMultiselectedItems = (layoutConfig, multiSelectedItems, direction, gridSize) => {
  let newLayoutConfig = { ...layoutConfig };
  const grouped = groupItemsByParentId(multiSelectedItems);
  const updatedMultiSelectedItems = {};

  Object.entries(grouped).forEach(([id, { path, items }]) => {
    if (path) {
      const mainElement = findItemById(layoutConfig || [], id);
      const data = getValueByPath(mainElement, path[1]);
      const newItems = items.map(({ id: itemId, item }) => {
        const movedItem = moveSelectedItemInLayout(item, direction, gridSize);
        updatedMultiSelectedItems[itemId] = {
          ...multiSelectedItems[itemId],
          item: movedItem
        };
        return movedItem;
      });
      const newLayout = updateBlockInLayout(data, newItems);
      const config = setValueByPath(mainElement, path[1], newLayout);
      newLayoutConfig = updateBlockInLayout(layoutConfig, config);
    } else {
      const newItems = items.map(({ id: itemId, item }) => {
        const movedItem = moveSelectedItemInLayout(item, direction, gridSize);
        updatedMultiSelectedItems[itemId] = {
          ...multiSelectedItems[itemId],
          item: movedItem
        };
        return movedItem;
      });
      newLayoutConfig = updateBlockInLayout(layoutConfig, newItems);
    }
  });

  return {
    newLayoutConfig,
    updatedMultiSelectedItems
  };
};

export const groupItemsByParentId = (selectedItemsMap) => {
  if (!selectedItemsMap || Object.keys(selectedItemsMap).length === 0) return {};

  const grouped = {};

  Object.entries(selectedItemsMap).forEach(([id, { item, otherInfo }]) => {
    const parentId = otherInfo?.path ? otherInfo.path[0] : "top-level";

    if (!grouped[parentId]) {
      grouped[parentId] = {
        path: otherInfo?.path || null,
        items: []
      };
    }

    grouped[parentId].items.push({
      id,
      item,
      otherInfo
    });
  });

  return grouped;
};

export const updateTableConfig = (layoutConfig, id, subType, config) => {
  const newTable = findItemById(layoutConfig, id);

  if (!newTable) return config;

  let updatedConfig = { ...newTable.config };

  if (subType === "edit_column_cell") {
    updatedConfig.config.columns = newTable.config.config.columns.map((column) => {
      if (column.cell_id === config.cell_id) {
        return { ...column, ...config };
      }
      return column;
    });
    return updatedConfig;
  } else if (subType === "edit_row_cell") {
    let rowUpdated = false;

    for (let i = 0; i < newTable.config.config.rows.length; i++) {
      const row = newTable.config.config.rows[i];
      for (let j = 0; j < row.length; j++) {
        const cell = row[j];

        if (cell.cell_id === config.cell_id) {
          row[j] = { ...cell, ...config };
          rowUpdated = true;
          break;
        }
      }
      if (rowUpdated) break;
    }

    if (rowUpdated) {
      updatedConfig.config.rows = [...newTable.config.config.rows];
    }
    return updatedConfig;
  } else {
    return config;
  }
};
export const convertPixelsToGrid = (layout, by) => {
  const processStyleValue = (value) => {
    if (value && value.endsWith("px")) {
      return Math.floor(parseInt(value, 10) / parseInt(by));
    }
    return value;
  };

  return layout.map((item) => {
    const updatedStyle = {
      ...item.style,
      width: item.type === "section" ? "100%" : processStyleValue(item.style?.width),
      height: item.type === "column" ? "100%" : processStyleValue(item.style?.height),
      left: processStyleValue(item.style?.left),
      top: processStyleValue(item.style?.top)
    };

    // Recursively process children if present
    const updatedChildren = item.children ? convertPixelsToGrid(item.children, by) : [];

    // Handle tabs if the item has them
    const updatedTabs = item.tabs
      ? item.tabs.map((tab) => ({
          ...tab,
          children: convertPixelsToGrid(tab.children || [], by)
        }))
      : item.tabs;

    // Handle catalog layout only for specific config types (e.g., rv_table)
    let updatedConfig = { ...item.config };
    if (item.config?.type === "rv_table" && Array.isArray(item.config.catalog?.layout)) {
      updatedConfig = {
        ...item.config,
        catalog: {
          ...item.config.catalog,
          layout: convertPixelsToGrid(item.config.catalog.layout, by)
        }
      };
    }

    if (item.config?.type === "field") {
      if (item.config?.field_type === "grid") {
        updatedConfig = {
          ...item.config,
          catalog: {
            ...item.config.catalog,
            layout: convertPixelsToGrid(item.config.catalog.layout, by)
          }
        };
      } else if (
        item.config?.field_type === "recordset-list" &&
        item.config?.catalog &&
        Array.isArray(item.config?.catalog?.layout)
      ) {
        updatedConfig = {
          ...item.config,
          catalog: {
            ...item.config.catalog,
            layout: convertPixelsToGrid(item.config.catalog.layout, by)
          }
        };
      }
    }

    // Return the updated item
    return {
      ...item,
      style: updatedStyle,
      children: updatedChildren,
      tabs: updatedTabs,
      config: updatedConfig
    };
  });
};

export const convertGridToPixels = (layout, by) => {
  const convertToPx = (value) => {
    if (typeof value === "number") {
      return `${value * parseInt(by)}px`; // Multiply by `by` and append "px"
    }
    return value; // Return unchanged for non-numeric values
  };

  return layout.map((item) => {
    const updatedStyle = {
      ...item.style,
      width: item.type === "section" ? "100%" : convertToPx(item.style?.width),
      height: item.type === "column" ? "100%" : convertToPx(item.style?.height),
      left: convertToPx(item.style?.left),
      top: convertToPx(item.style?.top)
    };

    // Recursively process children if present
    const updatedChildren = item.children ? convertGridToPixels(item.children, by) : [];

    // Handle tabs if the item has them
    const updatedTabs = item.tabs
      ? item.tabs.map((tab) => ({
          ...tab,
          children: convertGridToPixels(tab.children || [], by)
        }))
      : item.tabs;

    // Handle catalog layout only for specific config types (e.g., rv_table)
    let updatedConfig = { ...item.config };
    if (item.config?.type === "rv_table" && Array.isArray(item.config.catalog?.layout)) {
      updatedConfig = {
        ...item.config,
        catalog: {
          ...item.config.catalog,
          layout: item.config.catalog.layout.map((catalogItem) => ({
            ...catalogItem,
            style: {
              ...catalogItem.style,
              width: convertToPx(catalogItem.style?.width),
              height: convertToPx(catalogItem.style?.height),
              left: convertToPx(catalogItem.style?.left),
              top: convertToPx(catalogItem.style?.top)
            },
            children: catalogItem.children ? convertGridToPixels(catalogItem.children, by) : []
          }))
        }
      };
    }

    if (item.config?.type === "field") {
      if (item.config?.field_type === "grid") {
        updatedConfig = {
          ...item.config,
          catalog: {
            ...item.config.catalog,
            layout: item.config.catalog.layout.map((catalogItem) => ({
              ...catalogItem,
              style: {
                ...catalogItem.style,
                width: convertToPx(catalogItem.style?.width),
                height: convertToPx(catalogItem.style?.height),
                left: convertToPx(catalogItem.style?.left),
                top: convertToPx(catalogItem.style?.top)
              },
              children: catalogItem.children ? convertGridToPixels(catalogItem.children, by) : []
            }))
          }
        };
      } else if (
        item.config?.field_type === "recordset-list" &&
        item.config?.catalog &&
        Array.isArray(item.config?.catalog?.layout)
      ) {
        updatedConfig = {
          ...item.config,
          catalog: {
            ...item.config.catalog,
            layout: item.config.catalog.layout.map((catalogItem) => ({
              ...catalogItem,
              style: {
                ...catalogItem.style,
                width: convertToPx(catalogItem.style?.width),
                height: convertToPx(catalogItem.style?.height),
                left: convertToPx(catalogItem.style?.left),
                top: convertToPx(catalogItem.style?.top)
              },
              children: catalogItem.children ? convertGridToPixels(catalogItem.children, by) : []
            }))
          }
        };
      }
    }

    // Return the updated item
    return {
      ...item,
      style: updatedStyle,
      children: updatedChildren,
      tabs: updatedTabs,
      config: updatedConfig
    };
  });
};

export const getPositionedElementConfig = (type, device = "desktop") => {
  const resolvedType = ["section", "column", "block", "tab", "tabview"].includes(type) ? type : "element";
  const baseConfig = {
    id: generateObjectId(),
    classes: getDefaultClasses(type),
    children: [],
    type: resolvedType,
    config: {}
  };

  let style = getElementStyle(type, device);

  if (resolvedType === "tab" || resolvedType === "tabview") {
    baseConfig.classes = getDefaultClasses("section");
    baseConfig.tabs = getTabsConfig();
    baseConfig.type = "tab";
  }

  baseConfig.style = style;
  return baseConfig;
};

const getResponsiveValue = (device, values) => {
  return values[device] ?? values.desktop; // Fallback to desktop if device type is unknown
};

const getElementStyle = (type, device) => {
  const defaultStyle = { position: "absolute", width: "80px", height: "80px" };

  const sizeMap = {
    block: { width: "120px", height: "120px" },
    section: { width: "100%", height: "120px", position: "relative" },
    column: { width: "300px", height: "100%" },
    tab: { width: "200px", height: "180px" },
    tabview: { width: "480px", height: "360px" },
    rv_table: {
      width: getResponsiveValue(device, {
        mobile: "480px",
        tablet: "750px",
        desktop: "1500px"
      }),
      height: "396px"
    },
    activitycard: {
      width: getResponsiveValue(device, {
        mobile: "480px",
        tablet: "750px",
        desktop: "1500px"
      }),
      height: "396px"
    },
    rv_tab_filters: {
      width: getResponsiveValue(device, {
        mobile: "480px",
        tablet: "750px",
        desktop: "1500px"
      }),
      height: "42px"
    },
    rv_filters: {
      width: getResponsiveValue(device, {
        mobile: "36px",
        tablet: "240px",
        desktop: "360px"
      }),
      height: getResponsiveValue(device, {
        mobile: "36px",
        tablet: "42px",
        desktop: "48px"
      })
    },
    chart_filters: {
      width: getResponsiveValue(device, {
        mobile: "36px",
        tablet: "240px",
        desktop: "360px"
      }),
      height: getResponsiveValue(device, {
        mobile: "36px",
        tablet: "42px",
        desktop: "48px"
      })
    },
    label: { width: "120px", height: "24px" },
    field: { width: "120px", height: "24px" },
    json_to_table: { width: "432px", height: "228px" },
    rv_tile: { width: "120px", height: "24px" },
    search_box: { width: "168px", height: "40px" },
    rv_columns_manager: { width: "36px", height: "36px" },
    rv_table_catalog_toggler: { width: "36px", height: "36px" },
    rv_actions_list: { width: "36px", height: "36px" },
    download_file: { width: "36px", height: "36px" },
    dropdown: { width: "150px", height: "55px" },
    "form-field": { width: `452px`, height: `56px` },
    button: { width: "150px", height: "55px" },
    rv_action_button: { width: "150px", height: "48px" },
    rv_action_dropdown: { width: "150px", height: "55px" },
    icon: { width: "34px", height: "34px" },
    table: { width: "432px", height: "228px" },
    tableview: { width: "432px", height: "228px" },
    chart_tile: { width: "120px", height: "24px" },
    "custom-component": { width: "120px", height: "120px" },
    "dv-group": {
      width: getResponsiveValue(device, {
        mobile: "480px",
        tablet: "750px",
        desktop: "1500px"
      }),
      height: "396px"
    },
    form: { width: "432px", height: "228px" }
  };

  const style = sizeMap[type] || { width: defaultStyle.width, height: defaultStyle.height };
  return { ...defaultStyle, ...style };
};

const getTabsConfig = () => {
  return [
    {
      children: [
        {
          id: generateObjectId(),
          type: "full",
          style: {
            width: "100%",
            height: "100%",
            position: "absolute"
          },
          children: [
            {
              id: generateObjectId(),
              type: "section",
              style: { width: "100%", height: "120px", position: "relative" },
              classes: [],
              children: []
            }
          ]
        }
      ],
      id: generateObjectId(),
      name: "Tab 1"
    },
    {
      children: [
        {
          id: generateObjectId(),
          type: "full",
          style: {
            width: "100%",
            height: "100%",
            position: "absolute"
          },
          children: [
            {
              id: generateObjectId(),
              type: "section",
              style: { width: "100%", height: "120px", position: "relative" },
              classes: getDefaultClasses("section"),
              children: []
            }
          ]
        }
      ],
      id: generateObjectId(),
      name: "Tab 2"
    }
  ];
};

const getDefaultClasses = (type) => {
  switch (type) {
    case "section":
      // return ["z-shadow-none"];
      return [];
    case "column":
    case "block":
      return [
        // "z-bg-light",
        // "z-text-primary",
        // "z-border-solid",
        // "z-border-size-sm",
        // "z-border-secondary",
        // "z-shadow-none",
        // "z-border-radius-none"
      ];
    case "label":
    case "file_viewer":
      return [
        // "z-text-primary", "z-bg-transparent", "z-font-sm", "z-text-no-decoration"
      ];
    case "button":
    case "dropdown":
      return [
        // "z-btn", "z-btn-primary"
      ];
    default:
      return [];
  }
};

export const getOffset = (e) => {
  const offsetX = e.clientX - e.target.getBoundingClientRect().left;
  const offsetY = e.clientY - e.target.getBoundingClientRect().top;
  return { x: offsetX, y: offsetY };
};

export const getDeepCopy = (data) => {
  return JSON.parse(JSON.stringify(data));
};

export const getElements = (layout, screenBlocks) => {
  const result = [];

  const traverse = (items) => {
    items.forEach((item) => {
      // Check for matching element type
      if (item?.type === "element" && item?.config && screenBlocks.includes(item.config?.type)) {
        result.push(item.config);
      }

      // Traverse tab children
      if (item.type === "tab" && Array.isArray(item.tabs)) {
        item.tabs.forEach((tab) => {
          if (Array.isArray(tab.children)) {
            traverse(tab.children);
          }
        });
      }

      // Traverse normal children
      if (Array.isArray(item.children)) {
        traverse(item.children);
      }
    });
  };

  traverse(layout);
  return result;
};

export const expandToSelectedItem = (selectedItem, nodes, path = [], otherInfo) => {
  if (!selectedItem || !nodes) return null;

  for (const node of nodes) {
    const currentPath = [...path, node.id];
    if (node.id === selectedItem.id) {
      return currentPath;
    }
    if (otherInfo && otherInfo.path) {
      if (node.id === otherInfo.path[0]) {
        const pathArray = getValueByPath(node, otherInfo.path[1]);
        const childPath = expandToSelectedItem(selectedItem, pathArray, currentPath, otherInfo);
        if (childPath) return childPath;
      }
    }
    if (node.children) {
      const childPath = expandToSelectedItem(selectedItem, node.children, currentPath, otherInfo);
      if (childPath) return childPath;
    }
    if (node.tabs) {
      const tabPath = expandToSelectedItem(selectedItem, node.tabs, currentPath, otherInfo);
      if (tabPath) return tabPath;
    }
  }
  return null;
};

export const runIfEditable = (fn, type) => {
  return (...args) => {
    if (type === "editable") {
      fn(...args);
    }
  };
};

export function getMaxBottom(elements, parentTop = 0) {
  let maxBottom = parentTop;
  let totalSectionHeight = 0;
  let hasSection = false;

  elements.forEach((el) => {
    if (!el || !el.style) return;

    const top = el.style.top ? parseInt(el.style.top, 10) : 0;
    const height = el.style.height ? parseInt(el.style.height, 10) : 0;
    const absoluteTop = parentTop + top;
    const bottom = absoluteTop + height;

    if (el.type === "section") {
      totalSectionHeight += height;
      hasSection = true;
    } else {
      maxBottom = Math.max(maxBottom, bottom);
    }

    if (el.children?.length) {
      maxBottom = Math.max(maxBottom, getMaxBottom(el.children, absoluteTop));
    }
  });

  return hasSection ? Math.max(parentTop + totalSectionHeight, maxBottom) : maxBottom;
}

export function adjustSectionHeights(layout) {
  const DEFAULT_SECTION_HEIGHT = 120; // Default height for empty sections
  const TAB_HEADER_HEIGHT = 36; // Height of the tab header bar

  function updateHeights(elements, parentTop = 0, parentContainer = null) {
    elements.forEach((el) => {
      if (!el || !el.style) return;

      if (el.children?.length) {
        updateHeights(el.children, parentTop + (el.style.top ? parseInt(el.style.top, 10) : 0), el);
      }

      let maxBottom = el.children?.length ? getMaxBottom(el.children, parentTop) : parentTop + DEFAULT_SECTION_HEIGHT;

      const computedHeight = maxBottom - parentTop;
      const currentHeight = el.style.height ? parseInt(el.style.height, 10) : 0;

      if (el.type === "section") {
        if (currentHeight !== computedHeight) {
          el.style.height = `${computedHeight}px`;
        }

        if (parentContainer) {
          const parentHeight = parentContainer.style.height ? parseInt(parentContainer.style.height, 10) : 0;
          if (parentHeight < maxBottom) {
            parentContainer.style.height = `${maxBottom}px`;
          }
        }
      }

      if (el.type === "column") {
        el.style.height = "100%";
      }

      if (el.type === "tab") {
        let maxTabHeight = 0;

        el.tabs.forEach((tab) => {
          if (tab.children?.length) {
            tab.children.forEach((fullElement) => {
              if (fullElement.type === "full") {
                updateHeights(
                  fullElement.children,
                  parentTop + (fullElement.style.top ? parseInt(fullElement.style.top, 10) : 0),
                  fullElement
                );

                const tabBottom = getMaxBottom(fullElement.children, parentTop);
                maxTabHeight = Math.max(maxTabHeight, tabBottom - parentTop);
              }
            });
          }
        });

        if (maxTabHeight > 0) {
          el.style.height = `${Math.max(parseInt(el.style.height), maxTabHeight + TAB_HEADER_HEIGHT)}px`;
        }
      }
    });
  }

  const copyLayout = JSON.parse(JSON.stringify(layout));
  updateHeights(copyLayout);
  return copyLayout;
}

export function adjustHeightsWithGrid(layout, grid = 8) {
  const DEFAULT_SECTION_HEIGHT = 120;
  const TAB_HEADER_HEIGHT = 36;

  function snapToGrid(value, gridSize) {
    return Math.round(value / gridSize) * gridSize;
  }

  function getMaxBottom(children, parentTop) {
    let maxBottom = 0;
    for (const child of children) {
      if (!child?.style) continue;

      const top = parseInt(child.style.top || "0", 10);
      const height = parseInt(child.style.height || "0", 10);
      const bottom = parentTop + top + height;
      maxBottom = Math.max(maxBottom, bottom);
    }
    return maxBottom;
  }

  function updateHeights(elements, parentTop = 0, parentContainer = null) {
    elements.forEach((el) => {
      if (!el || !el.style) return;

      // Snap left and top if defined
      if (typeof el.style.left === "string") {
        el.style.left = `${snapToGrid(parseInt(el.style.left, 10), grid)}px`;
      }
      if (typeof el.style.top === "string") {
        el.style.top = `${snapToGrid(parseInt(el.style.top, 10), grid)}px`;
      }

      const elTop = parseInt(el.style.top || "0", 10);
      const elAbsTop = parentTop + elTop;

      // Snap width if present (except for section)
      if (el.style.width && el.type !== "section") {
        el.style.width = `${snapToGrid(parseInt(el.style.width, 10), grid)}px`;
      }

      if (el.children?.length) {
        updateHeights(el.children, elAbsTop, el);
      }

      let maxBottom = el.children?.length ? getMaxBottom(el.children, elAbsTop) : elAbsTop + DEFAULT_SECTION_HEIGHT;

      let computedHeight = maxBottom - elAbsTop;
      computedHeight = snapToGrid(computedHeight, grid);

      if (el.type === "section") {
        const currentHeight = parseInt(el.style.height || "0", 10);
        if (currentHeight !== computedHeight) {
          el.style.height = `${computedHeight}px`;
        }

        if (parentContainer) {
          const parentHeight = parseInt(parentContainer.style.height || "0", 10);
          const snappedBottom = snapToGrid(maxBottom, grid);
          if (parentHeight < snappedBottom - parentTop) {
            parentContainer.style.height = `${snappedBottom - parentTop}px`;
          }
        }
      }

      if (el.type === "column") {
        el.style.height = "100%"; // Skip snapping for column height
      } else if (el.style.height) {
        el.style.height = `${snapToGrid(parseInt(el.style.height, 10), grid)}px`;
      }

      if (el.type === "tab") {
        let maxTabHeight = 0;

        el.tabs.forEach((tab) => {
          if (tab.children?.length) {
            tab.children.forEach((fullElement) => {
              if (fullElement.type === "full") {
                const fullTop = parseInt(fullElement.style.top || "0", 10);
                const fullAbsTop = elAbsTop + fullTop;

                updateHeights(fullElement.children, fullAbsTop, fullElement);

                const tabBottom = getMaxBottom(fullElement.children, fullAbsTop);
                maxTabHeight = Math.max(maxTabHeight, tabBottom - elAbsTop);
              }
            });
          }
        });

        if (maxTabHeight > 0) {
          const totalTabHeight = snapToGrid(maxTabHeight + TAB_HEADER_HEIGHT, grid);
          const currentTabHeight = parseInt(el.style.height || "0", 10);
          if (currentTabHeight < totalTabHeight) {
            el.style.height = `${totalTabHeight}px`;
          }
        }
      }
    });
  }

  const copyLayout = JSON.parse(JSON.stringify(layout));
  updateHeights(copyLayout);
  return copyLayout;
}

export const parseCustomCss = (custom_css) => {
  let customStyles = {};
  if (custom_css) {
    try {
      customStyles = JSON.parse(custom_css);
    } catch (error) {
      // console.error("Error in parsing custom css");
    }
  }
  return customStyles;
};

export function getValueByPath(obj, path) {
  return path.split(".").reduce((acc, key) => {
    return acc && acc[key] !== undefined ? acc[key] : undefined;
  }, obj);
}

export function setValueByPath(obj, path, value) {
  const keys = path.split(".");

  function helper(current, index) {
    const key = keys[index];
    const isLast = index === keys.length - 1;

    // const isIndex = !isNaN(Number(key)); // Check if it's an array index
    const nextKey = keys[index + 1];
    const nextIsIndex = !isNaN(Number(nextKey));

    let nextLevel = current?.[key];

    if (isLast) {
      if (Array.isArray(current)) {
        const copy = [...current];
        copy[key] = value;
        return copy;
      }
      return { ...current, [key]: value };
    }

    // Decide whether next level should be an array or object
    if (nextLevel === undefined) {
      nextLevel = nextIsIndex ? [] : {};
    }

    const updatedNext = helper(nextLevel, index + 1);

    if (Array.isArray(current)) {
      const copy = [...current];
      copy[key] = updatedNext;
      return copy;
    }

    return { ...current, [key]: updatedNext };
  }

  return helper(obj, 0);
}

export function getScreenWidth({ device, metadata }) {
  if (device in SCREEN_WIDTHS) {
    return SCREEN_WIDTHS[device];
  }

  const { grid_size, total_grid } = metadata || {};
  const width = grid_size * total_grid;
  return width?.toString() || "0";
}

export const alignItems = (layoutConfig, selectedItemsMap, property) => {
  if (!selectedItemsMap || Object.keys(selectedItemsMap).length === 0) return layoutConfig;

  let newLayout = JSON.parse(JSON.stringify(layoutConfig));
  const grouped = groupItemsByParentId(selectedItemsMap);

  let minPosition = Infinity;
  Object.values(selectedItemsMap).forEach(({ item }) => {
    const position = parseInt(item.style[property], 10);
    if (!isNaN(position) && position < minPosition) {
      minPosition = position;
    }
  });

  const targetPosition = `${minPosition}px`;

  Object.entries(grouped).forEach(([id, { path, items }]) => {
    if (path) {
      const mainElement = findItemById(newLayout, id);
      const data = getValueByPath(mainElement, path[1]);

      const newItems = items.map(({ item }) => ({
        ...item,
        style: {
          ...item.style,
          [property]: targetPosition
        }
      }));

      const newLayoutData = updateBlockInLayout(data, newItems);
      const config = setValueByPath(mainElement, path[1], newLayoutData);
      newLayout = updateBlockInLayout(newLayout, config);
    } else {
      const newItems = items.map(({ item }) => ({
        ...item,
        style: {
          ...item.style,
          [property]: targetPosition
        }
      }));
      newLayout = updateBlockInLayout(newLayout, newItems);
    }
  });

  return newLayout;
};

export const alignItemsToLeft = (layoutConfig, selectedItemsMap) => {
  return alignItems(layoutConfig, selectedItemsMap, "left");
};

export const alignItemsToTop = (layoutConfig, selectedItemsMap) => {
  return alignItems(layoutConfig, selectedItemsMap, "top");
};

export const updateSelectedItemsPosition = (multiSelectedItems, newLayout, property) => {
  return Object.entries(multiSelectedItems).reduce((acc, [id, data]) => {
    acc[id] = {
      ...data,
      item: {
        ...data.item,
        style: {
          ...data.item.style,
          [property]: findItemById(newLayout, id)?.style?.[property] || data.item.style[property]
        }
      }
    };
    return acc;
  }, {});
};

const distributeItems = (layoutConfig, selectedItemsMap, axis) => {
  if (!selectedItemsMap || Object.keys(selectedItemsMap).length <= 2) return layoutConfig;

  let newLayout = JSON.parse(JSON.stringify(layoutConfig));
  const grouped = groupItemsByParentId(selectedItemsMap);

  Object.entries(grouped).forEach(([id, { path, items }]) => {
    const newItems = distributeItemsAlongAxis(items, axis);

    if (path) {
      const mainElement = findItemById(newLayout, id);
      const data = getValueByPath(mainElement, path[1]);
      const newLayoutData = updateBlockInLayout(data, newItems);
      const config = setValueByPath(mainElement, path[1], newLayoutData);
      newLayout = updateBlockInLayout(newLayout, config);
    } else {
      newLayout = updateBlockInLayout(newLayout, newItems);
    }
  });

  return newLayout;
};

const distributeItemsAlongAxis = (items, axis) => {
  const positionProperty = axis === "vertical" ? "top" : "left";
  const sizeProperty = axis === "vertical" ? "height" : "width";

  // Sort items by position along the specified axis
  const sortedItems = [...items].sort((a, b) => {
    const aPosition = parseInt(a.item.style[positionProperty], 10);
    const bPosition = parseInt(b.item.style[positionProperty], 10);
    return aPosition - bPosition;
  });

  const firstItem = sortedItems[0];
  const lastItem = sortedItems[sortedItems.length - 1];

  // Calculate start and end boundaries considering element sizes
  const startPosition = parseInt(firstItem.item.style[positionProperty], 10);
  // const firstItemSize = parseInt(firstItem.item.style[sizeProperty], 10) || 0;

  const lastItemPosition = parseInt(lastItem.item.style[positionProperty], 10);
  const lastItemSize = parseInt(lastItem.item.style[sizeProperty], 10) || 0;
  const endPosition = lastItemPosition + lastItemSize;

  // Calculate available space for distribution
  const totalOccupiedSpace = sortedItems.reduce((sum, item) => {
    return sum + (parseInt(item.item.style[sizeProperty], 10) || 0);
  }, 0);

  const totalAvailableSpace = endPosition - startPosition;
  const totalGapSpace = totalAvailableSpace - totalOccupiedSpace;
  const gapBetweenItems = sortedItems.length > 1 ? totalGapSpace / (sortedItems.length - 1) : 0;

  return sortedItems.map((item, index) => {
    // Keep first item in original position
    if (index === 0) {
      return item.item;
    }

    // Calculate new position based on previous items and gaps
    let newPosition = startPosition;
    for (let i = 0; i < index; i++) {
      const prevItemSize = parseInt(sortedItems[i].item.style[sizeProperty], 10) || 0;
      newPosition += prevItemSize + gapBetweenItems;
    }

    // Keep last item in original position (optional - you might want to comment this out)
    if (index === sortedItems.length - 1) {
      return item.item;
    }

    return {
      ...item.item,
      style: {
        ...item.item.style,
        [positionProperty]: `${Math.round(newPosition)}px`
      }
    };
  });
};

export const distributeItemsVertically = (layoutConfig, selectedItemsMap) => {
  return distributeItems(layoutConfig, selectedItemsMap, "vertical");
};

export const distributeItemsHorizontally = (layoutConfig, selectedItemsMap) => {
  return distributeItems(layoutConfig, selectedItemsMap, "horizontal");
};

export const addNewSection = (type, layoutConfig, setLayoutConfig, setSelectedItem) => {
  if (type !== "editable") return;

  const newSection = getPositionedElementConfig("section");
  const newLayoutConfig = [...layoutConfig, newSection];
  setLayoutConfig(newLayoutConfig);
  setSelectedItem(newSection);
  // changedLayout();
};

export const addNewSectionAtTop = (type, layoutConfig, setLayoutConfig, setSelectedItem) => {
  if (type !== "editable") return;

  const newSection = getPositionedElementConfig("section");
  const newLayoutConfig = [newSection, ...layoutConfig];
  setLayoutConfig(newLayoutConfig);
  setSelectedItem(newSection);
};

export function checkItemsTypeUniformity(elementsMap) {
  const types = new Set();

  for (const key in elementsMap) {
    const itemType = elementsMap[key]?.item?.config?.type;
    if (itemType) {
      types.add(itemType);
    }
  }

  const allSameType = types.size === 1;
  return {
    allSameType,
    type: allSameType ? [...types][0] : null
  };
}
